import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  CircularProgress,
  Chip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SearchBar from '../components/SearchBar';
import EventCard from '../components/EventCard';
import EventLightbox from '../components/EventLightbox';
import ArticleCard from '../components/ArticleCard';
import { featureAPI, locationAPI } from '../services/api';
import { Feature, Location, Event, Article } from '../types';
import { updateSEOTags } from '../utils/seoUtils';

const Home: React.FC = () => {
  const [features, setFeatures] = useState<Feature[]>([]);
  const [featuredLocations, setFeaturedLocations] = useState<Location[]>([]);
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const navigate = useNavigate();

  const handleEventCardClick = (event: Event) => {
    // On home page, always open modal on both mobile and desktop
    setSelectedEvent(event);
    setLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
    setSelectedEvent(null);
  };

  useEffect(() => {
    const fetchHomeData = async () => {
      try {
        const [featuresData, locationsData, articlesData] = await Promise.all([
          featureAPI.getHomeFeatures(),
          locationAPI.getFeatured(),
          import('../data/articles.json').then(module => module.default).catch(() => ({ articles: [] }))
        ]);
        setFeatures(featuresData);
        setFeaturedLocations(locationsData);
        setArticles((articlesData.articles as Article[]) || []);
      } catch (error) {
        console.error('Error fetching home data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchHomeData();
  }, []);

  // Update SEO tags for homepage
  useEffect(() => {
    if (features.length > 0) {
      // Collect all SEO keywords from homepage features
      const allKeywords = features.reduce((keywords: string[], feature) => {
        if (feature.seoKeywords) {
          keywords.push(...feature.seoKeywords);
        }
        return keywords;
      }, []);

      // Remove duplicates and limit to most relevant
      const uniqueKeywords = Array.from(new Set(allKeywords)).slice(0, 15);

      // Create homepage-specific SEO content
      const title = "FindFunNow - Search Activities, Events, and Hotels Worldwide";
      const description = uniqueKeywords.length > 0 
        ? `Discover amazing activities and experiences worldwide. Find ${uniqueKeywords.slice(0, 5).join(', ')} and more. Book hotels, events, and unique experiences globally.`
        : "Discover amazing activities, events, and accommodations worldwide. Book your perfect travel experience with FindFunNow.";

      // Update SEO tags
      updateSEOTags({
        title,
        description,
        keywords: uniqueKeywords.length > 0 ? uniqueKeywords : ['travel', 'activities', 'events', 'hotels', 'vacation', 'booking'],
        locationName: 'Worldwide'
      });
    }
  }, [features]);

  return (
    <Box>
      <Box
        sx={{
          backgroundImage: 'url(/hero.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          minHeight: '70vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(14, 16, 121, 0.8) 20%, rgba(71, 26, 113, 0.6) 80%, rgba(12, 71, 144, 0.4) 100%)',
            zIndex: 1,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 30% 40%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)',
            zIndex: 2,
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 3, textAlign: 'center' }}>
          <Box
            sx={{
              opacity: 0,
              animation: 'fadeInUp 1.2s ease-out 0.3s forwards',
              '@keyframes fadeInUp': {
                from: {
                  opacity: 0,
                  transform: 'translateY(40px)',
                },
                to: {
                  opacity: 1,
                  transform: 'translateY(0)',
                },
              },
            }}
          >
            <Typography
              variant="h1"
              component="h1"
              sx={{
                color: 'white',
                textAlign: 'center',
                mb: 3,
                fontWeight: 800,
                fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem' },
                textShadow: '0 4px 20px rgba(0,0,0,0.3)',
                background: 'linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '-0.02em',
              }}
            >
             Where are you headed?
            </Typography>
            <Typography
              variant="h2"
              component="span"
              sx={{
                color: 'white',
                display: 'block',
                mb: 4,
                fontWeight: 800,
                fontSize: { xs: '1.5rem', sm: '1.5rem', md: '1.5rem' },
                textShadow: '0 4px 20px rgba(0,0,0,0.3)',
                background: 'linear-gradient(135deg, #f6d9b5ff 0%, #f8d496ff 50%, #ffe5c7ff 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '-0.02em',
              }}
            >
              Discover unforgettable events, tours, and activities worldwide.
            </Typography>
          </Box>
          
          <Box
            sx={{
              opacity: 0,
              animation: 'fadeInUp 1.2s ease-out 0.6s forwards',
              maxWidth: 600,
              mx: 'auto',
            }}
          >
            <SearchBar variant="hero" />
          </Box>

          <Typography
            variant="body1"
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              textAlign: 'center',
              mt: 4,
              fontSize: '1.2rem',
              opacity: 0,
              animation: 'fadeInUp 1.2s ease-out 0.9s forwards',
              textShadow: '0 2px 10px rgba(0,0,0,0.3)',
            }}
          >
            {/* Discover amazing experiences, tours, and activities worldwide */}
          </Typography>
        </Container>
      </Box>

      <Box sx={{ 
        background: 'linear-gradient(180deg, #fafafa 0%, #ffffff 50%, #f8fafc 100%)',
        py: 6,
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: 'linear-gradient(90deg, transparent 0%, rgba(99, 102, 241, 0.3) 50%, transparent 100%)',
        },
      }}>
        <Container maxWidth="lg">
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
              <CircularProgress 
                size={60} 
                thickness={4}
                sx={{ 
                  color: 'primary.main',
                  animation: 'pulse 2s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%': { opacity: 1 },
                    '50%': { opacity: 0.5 },
                    '100%': { opacity: 1 },
                  },
                }}
              />
            </Box>
          ) : (
            <>
              {/* Featured Locations Section */}
              {featuredLocations.length > 0 && (
                <Box sx={{ mb: 8 }}>
                  <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Typography 
                      variant="h4" 
                      component="h2" 
                      sx={{ 
                        mb: 2,
                        fontWeight: 700,
                        background: 'linear-gradient(135deg, #1f2937 0%, #6366f1 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      Featured Destinations
                    </Typography>
                    <Box 
                      sx={{ 
                        width: 60, 
                        height: 4, 
                        background: 'linear-gradient(90deg, #6366f1, #f59e0b)',
                        borderRadius: 2,
                        mx: 'auto',
                        mb: 4,
                      }} 
                    />
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center' }}>
                    {featuredLocations.map((location) => (
                      <Chip
                        key={location.uniqueId}
                        label={location.name}
                        onClick={() => navigate(`/search?location=${encodeURIComponent(location.name)}`)}
                        sx={{
                          borderRadius: '20px',
                          px: 2,
                          py: 1,
                          fontWeight: 600,
                          fontSize: '1rem',
                          height: 'auto',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                          color: 'white',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 10px 25px -5px rgba(99, 102, 241, 0.4), 0 4px 6px -2px rgba(99, 102, 241, 0.1)',
                          },
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {features.length === 0 ? (
                <>
                  {['Top things to do in Europe', 'West coast adventures', 'Best beer tours in Germany'].map((title, index) => (
                    <Box key={title} sx={{ mb: 10 }}>
                      <Box sx={{ textAlign: 'center', mb: 6 }}>
                        <Typography 
                          variant="h4" 
                          component="h2" 
                          sx={{ 
                            mb: 2,
                            fontWeight: 700,
                            background: 'linear-gradient(135deg, #1f2937 0%, #6366f1 100%)',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                          }}
                        >
                          {title}
                        </Typography>
                        <Box 
                          sx={{ 
                            width: 60, 
                            height: 4, 
                            background: 'linear-gradient(90deg, #6366f1, #f59e0b)',
                            borderRadius: 2,
                            mx: 'auto',
                            mb: 4,
                          }} 
                        />
                      </Box>
                      <Grid container spacing={4}>
                        {[1, 2, 3].map((i) => (
                          <Grid item xs={12} sm={6} md={4} key={i}>
                            <Box sx={{ 
                              height: 300, 
                              background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                              borderRadius: 5,
                              position: 'relative',
                              overflow: 'hidden',
                              '&::after': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: 'linear-gradient(45deg, rgba(99, 102, 241, 0.1), rgba(168, 85, 247, 0.1))',
                                animation: 'shimmer 2s ease-in-out infinite',
                                '@keyframes shimmer': {
                                  '0%': { opacity: 0.5 },
                                  '50%': { opacity: 1 },
                                  '100%': { opacity: 0.5 },
                                },
                              }
                            }} />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  ))}
                </>
              ) : (
                features.map((feature, index) => (
                  <Box 
                    key={feature._id} 
                    sx={{ 
                      mb: 10,
                      opacity: 0,
                      animation: `slideInUp 0.8s ease-out ${index * 0.2}s forwards`,
                      '@keyframes slideInUp': {
                        from: {
                          opacity: 0,
                          transform: 'translateY(60px)',
                        },
                        to: {
                          opacity: 1,
                          transform: 'translateY(0)',
                        },
                      },
                    }}
                  >
                    <Box sx={{ textAlign: 'center', mb: 6 }}>
                      <Typography 
                        variant="h4" 
                        component="h2" 
                        sx={{ 
                          mb: 2,
                          fontWeight: 700,
                          background: 'linear-gradient(135deg, #1f2937 0%, #6366f1 100%)',
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                        }}
                      >
                        {feature.title}
                      </Typography>
                      <Box 
                        sx={{ 
                          width: 60, 
                          height: 4, 
                          background: 'linear-gradient(90deg, #6366f1, #f59e0b)',
                          borderRadius: 2,
                          mx: 'auto',
                          mb: 4,
                        }} 
                      />
                    </Box>
                    {/* Desktop: Grid with multiple rows */}
                    <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                      <Grid container spacing={3}>
                        {feature.events?.map((event, eventIndex) => (
                          <Grid item md={4} key={event.uniqueId}>
                            <Box
                              sx={{
                                opacity: 0,
                                animation: `fadeInScale 0.6s ease-out ${eventIndex * 0.1 + 0.3}s forwards`,
                                '@keyframes fadeInScale': {
                                  from: {
                                    opacity: 0,
                                    transform: 'scale(0.95)',
                                  },
                                  to: {
                                    opacity: 1,
                                    transform: 'scale(1)',
                                  },
                                },
                              }}
                            >
                              <EventCard event={event} onCardClick={handleEventCardClick} variant="homepage" />
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>

                    {/* Mobile: Single column */}
                    <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                      <Grid container spacing={3}>
                        {feature.events?.map((event, eventIndex) => (
                          <Grid item xs={12} key={event.uniqueId}>
                            <Box
                              sx={{
                                opacity: 0,
                                animation: `fadeInScale 0.6s ease-out ${eventIndex * 0.1 + 0.3}s forwards`,
                                '@keyframes fadeInScale': {
                                  from: {
                                    opacity: 0,
                                    transform: 'scale(0.95)',
                                  },
                                  to: {
                                    opacity: 1,
                                    transform: 'scale(1)',
                                  },
                                },
                              }}
                            >
                              <EventCard event={event} onCardClick={handleEventCardClick} variant="homepage" />
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </Box>
                ))
              )}

              {/* Articles Section */}
              {articles.length > 0 && (
                <Box sx={{ mb: 8 }}>
                  <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Typography 
                      variant="h4" 
                      component="h2" 
                      sx={{ 
                        mb: 2,
                        fontWeight: 700,
                        background: 'linear-gradient(135deg, #1f2937 0%, #6366f1 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      Travel Articles
                    </Typography>
                    <Box 
                      sx={{ 
                        width: 60, 
                        height: 4, 
                        background: 'linear-gradient(90deg, #6366f1, #f59e0b)',
                        borderRadius: 2,
                        mx: 'auto',
                        mb: 4,
                      }} 
                    />
                  </Box>
                  <Grid container spacing={4}>
                    {articles.map((article, index) => (
                      <Grid item xs={12} sm={6} md={4} key={article.slug}>
                        <Box
                          sx={{
                            opacity: 0,
                            animation: `fadeInScale 0.6s ease-out ${index * 0.1 + 0.3}s forwards`,
                            '@keyframes fadeInScale': {
                              from: {
                                opacity: 0,
                                transform: 'scale(0.95)',
                              },
                              to: {
                                opacity: 1,
                                transform: 'scale(1)',
                              },
                            },
                          }}
                        >
                          <ArticleCard article={article} />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

            {features.length === 0 && (
              <>
                <Box sx={{ mb: 8 }}>
                  <Typography variant="h4" component="h2" sx={{ mb: 4 }}>
                    West coast adventures
                  </Typography>
                  <Grid container spacing={3}>
                    {[1, 2, 3].map((i) => (
                      <Grid item xs={12} sm={6} md={4} key={i}>
                        <Box sx={{ height: 300, bgcolor: 'grey.200', borderRadius: 2 }} />
                      </Grid>
                    ))}
                  </Grid>
                </Box>

                <Box sx={{ mb: 8 }}>
                  <Typography variant="h4" component="h2" sx={{ mb: 4 }}>
                    Best beer tours in Germany
                  </Typography>
                  <Grid container spacing={3}>
                    {[1, 2, 3].map((i) => (
                      <Grid item xs={12} sm={6} md={4} key={i}>
                        <Box sx={{ height: 300, bgcolor: 'grey.200', borderRadius: 2 }} />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </>
            )}
          </>
        )}
      </Container>
    </Box>
    
    <EventLightbox
      event={selectedEvent}
      open={lightboxOpen}
      onClose={handleCloseLightbox}
    />
    </Box>
  );
};

export default Home;